
import React, { useState } from 'react';
import { ParkingSpace, ReleaseRecord, Infringement, QueueEntry, UserRole } from '../types';
import { ICONS } from '../constants';

interface ParkingGridProps {
  spaces: ParkingSpace[];
  releases: ReleaseRecord[];
  onReportInfringement: (report: any) => void;
  infringements: Infringement[];
  queue: QueueEntry[];
  userRole: UserRole;
}

const ParkingGrid: React.FC<ParkingGridProps> = ({ 
  spaces, 
  releases, 
  infringements, 
  onReportInfringement, 
  queue,
  userRole
}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];

  const getMonday = (d: Date) => {
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const currentMonday = getMonday(new Date(today));
  const [selectedDate, setSelectedDate] = useState<string>(todayStr);

  const isOwnerOrAdmin = userRole === 'OWNER' || userRole === 'ADMIN';
  const calendarHorizonDays = isOwnerOrAdmin ? 28 : 14;

  const weekDays = Array.from({ length: calendarHorizonDays }).map((_, i) => {
    const d = new Date(currentMonday);
    d.setDate(currentMonday.getDate() + i);
    return d;
  }).filter(d => d.getDay() !== 0 && d.getDay() !== 6);

  return (
    <div className="space-y-10 text-black">
      <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-black uppercase tracking-widest text-slate-400">
            {isOwnerOrAdmin ? 'Monthly Outlook (4 Weeks)' : 'Standard Outlook (2 Weeks)'}
          </h3>
          <p className="text-[10px] font-bold text-slate-400">SELECT A WORKING DAY TO VIEW AVAILABILITY</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {weekDays.map((d) => {
            const ds = d.toISOString().split('T')[0];
            const isSelected = selectedDate === ds;
            const isPassed = ds < todayStr;
            const isToday = ds === todayStr;

            return (
              <button 
                key={ds} 
                onClick={() => !isPassed && setSelectedDate(ds)}
                disabled={isPassed}
                className={`group relative flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all ${
                  isPassed 
                    ? 'bg-slate-50 border-slate-50 opacity-40 cursor-not-allowed grayscale' 
                    : isSelected 
                      ? 'bg-accent-red border-accent-red text-white shadow-xl shadow-red-100' 
                      : 'bg-white border-slate-100 hover:border-accent-red text-slate-500'
                }`}
              >
                <span className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isSelected ? 'text-white' : 'text-slate-400 group-hover:text-accent-red'}`}>
                  {d.toLocaleDateString(undefined, { weekday: 'short' })}
                </span>
                <span className={`text-xl font-black ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                  {d.getDate()}
                </span>
                {isToday && !isSelected && (
                  <div className="absolute top-2 right-2 w-1.5 h-1.5 bg-accent-red rounded-full" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {spaces.map(s => {
          const rel = releases.find(r => r.spaceId === s.id && r.date === selectedDate);
          const inf = infringements.find(i => i.spaceId === s.id && i.status === 'PENDING');
          const isClaimed = rel?.isClaimed;
          const occupant = isClaimed ? rel.claimedBy : s.ownerName;

          return (
            <div key={s.id} className={`p-5 rounded-2xl border-2 flex flex-col justify-between h-40 transition-all ${
              rel 
                ? (isClaimed ? 'bg-emerald-50 border-emerald-500 shadow-sm shadow-emerald-100' : 'bg-amber-50 border-amber-500 shadow-sm shadow-amber-100') 
                : 'bg-white border-slate-100 shadow-sm'
            }`}>
              <div className="flex justify-between items-start">
                <span className="text-[10px] font-black text-slate-500 bg-slate-50 px-2 py-1 rounded border border-slate-100">{s.displayLabel}</span>
                {inf && <div className="w-3 h-3 bg-rose-600 rounded-full animate-pulse shadow-lg shadow-rose-200" title="Violation reported" />}
              </div>

              <div>
                <h4 className="font-black text-sm text-slate-900 leading-tight truncate" title={occupant}>
                  {occupant}
                </h4>
                <div className="flex flex-col gap-0.5 mt-1">
                  <p className={`text-[9px] font-black uppercase tracking-widest ${isClaimed ? 'text-emerald-600' : (rel ? 'text-amber-600' : 'text-slate-400')}`}>
                    {isClaimed ? 'Allocated' : (rel ? 'Released' : 'Designated')}
                  </p>
                  {isClaimed && s.ownerName && s.ownerName !== 'Unassigned' && (
                    <p className="text-[7px] font-bold text-slate-400 uppercase truncate">
                      Owner: {s.ownerName}
                    </p>
                  )}
                </div>
              </div>

              {isClaimed && (
                <div className="bg-emerald-600/10 px-2 py-1 rounded-lg">
                  <p className="text-[8px] font-black text-emerald-700 uppercase leading-none">Booked via Queue</p>
                </div>
              )}

              <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden mt-2">
                <div className={`h-full transition-all duration-500 ${isClaimed ? 'bg-emerald-500' : (rel ? 'bg-amber-500' : 'bg-accent-red')}`} style={{width: '100%'}} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ParkingGrid;
